import {Box, IconButton, Typography} from '@mui/material';
import ArrowBackIcon from '@icons/ArrowBackIcon';
import {useRef, useState} from 'react';
import type {Swiper as SwiperInstance} from 'swiper';
import {A11y, Pagination} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

interface AdImageGalleryProps {
	images: string[];
}

export default function AdImageGallery({images}: AdImageGalleryProps) {
	const swiperRef = useRef<SwiperInstance | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(images.length <= 1);

	const updateNavState = (swiper: SwiperInstance) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<Box className="w-full relative">
			<Swiper
				className="ad-gallery-swiper rounded-xl"
				modules={[Pagination, A11y]}
				spaceBetween={12}
				slidesPerView={1}
				observer
				observeParents
				pagination={{
					clickable: true,
					dynamicBullets: true
				}}
				a11y={{
					prevSlideMessage: 'تصویر قبلی',
					nextSlideMessage: 'تصویر بعدی'
				}}
				onSwiper={(swiper) => {
					swiperRef.current = swiper;
					updateNavState(swiper);
				}}
				onSlideChange={updateNavState}
			>
				{images.map((image, index) => (
					<SwiperSlide key={image}>
						<Box
							sx={{
								bgcolor: 'white',
								borderRadius: 2,
								overflow: 'hidden'
							}}
						>
							<img
								src={image}
								alt={`تصویر ${index + 1}`}
								className="w-full h-auto max-h-[60vh] object-contain mx-auto block"
							/>
						</Box>
					</SwiperSlide>
				))}
			</Swiper>

			{images.length > 1 && (
				<>
					<IconButton
						aria-label="تصویر قبلی"
						className="absolute top-1/2 -translate-y-1/2 z-10 rtl:right-1 ltr:left-1"
						onClick={() => swiperRef.current?.slidePrev()}
						disabled={isBeginning}
						sx={{
							bgcolor: 'background.paper',
							boxShadow: 1,
							'&:hover': {bgcolor: 'background.paper'}
						}}
					>
						<ArrowBackIcon/>
					</IconButton>
					<IconButton
						aria-label="تصویر بعدی"
						className="absolute top-1/2 -translate-y-1/2 z-10 rtl:left-1 ltr:right-1"
						onClick={() => swiperRef.current?.slideNext()}
						disabled={isEnd}
						sx={{
							bgcolor: 'background.paper',
							boxShadow: 1,
							'&:hover': {bgcolor: 'background.paper'},
							'& svg': {transform: 'scaleX(-1)'}
						}}
					>
						<ArrowBackIcon/>
					</IconButton>
				</>
			)}

			<Typography
				variant="caption"
				color="text.secondary"
				className="block text-center mt-2"
			>
				برای مشاهده سایر تصاویر، بکشید یا از فلش‌ها استفاده کنید
			</Typography>
		</Box>
	);
}
